import { type ReactElement, useState, useCallback, useMemo, useEffect } from 'react';
import { useFetcher } from 'react-router';
import { useTranslation } from 'react-i18next';
import { useConfig } from '@salesforce/storefront-next-runtime/config';
import type { AppConfig } from '@/types/config';
import type { ConsumerLookupResponse } from '@/routes/action.consumer-lookup';
import type { AuthorizePasswordlessEmailResponse } from '@/routes/action.authorize-passwordless-email';
import { resourceRoutes } from '@/route-paths';
import {
    getPasswordlessCodeDeliveryChannels,
    shouldShowPasswordlessCodeDeliveryStep,
} from '@/lib/auth/passwordless-code-delivery-channels';
import { useAuth } from '@/providers/auth';
import { LoginFlowVariantProvider, type LoginFlowVariant } from './variant';
import EmailStep from './steps/email-step';
import AuthMethodsStep from './steps/auth-methods-step';
import PasswordStep from './steps/password-step';
import SendCodeStep from './steps/send-code-step';
import VerifyCodeStep from './steps/verify-code-step';
import AccountStep from './steps/account-step';

export type LoginFlowStep = 'account' | 'email' | 'auth-methods' | 'password' | 'send-code' | 'verify-code';

export interface LoginFlowProps {
    variant: LoginFlowVariant;
    returnUrl: string;
    initialStep?: LoginFlowStep;
    initialEmail?: string;
    initialError?: string;
    onSuccess: () => void;
}

function LoginFlowContent({
    returnUrl,
    initialStep,
    initialEmail,
    initialError,
    onSuccess,
}: Omit<LoginFlowProps, 'variant'>): ReactElement {
    const session = useAuth();
    const isAuthenticated = useMemo(
        () => Boolean(session?.userType === 'registered' && session?.customerId),
        [session]
    );
    const [step, setStep] = useState<LoginFlowStep>(() => initialStep ?? (isAuthenticated ? 'account' : 'email'));
    const [email, setEmail] = useState(initialEmail ?? '');
    const [emailError, setEmailError] = useState<string | undefined>(initialError);
    const config = useConfig<AppConfig>();
    const { t } = useTranslation('login');

    const codeDeliveryChannels = useMemo(() => getPasswordlessCodeDeliveryChannels(config), [config]);
    const showCodeDeliveryStep = shouldShowPasswordlessCodeDeliveryStep(codeDeliveryChannels);
    const sendCodeFetcher = useFetcher<AuthorizePasswordlessEmailResponse>();

    const isSendingCode = sendCodeFetcher.state === 'submitting' || sendCodeFetcher.state === 'loading';
    const sendCodeError =
        sendCodeFetcher.state === 'idle' && sendCodeFetcher.data?.success === false
            ? sendCodeFetcher.data.error?.code === 'RATE_LIMITED'
                ? t('sheet.otpRateLimitError', { defaultValue: 'Too many attempts. Please wait a few minutes.' })
                : t('sheet.otpSendError', { defaultValue: 'Unable to send code. Please try again.' })
            : undefined;

    const otpLength = config.auth?.otpLength ?? 8;

    const handleLookupResult = useCallback(
        (result: ConsumerLookupResponse) => {
            setEmail(result.email);
            if (!result.exists) {
                setEmailError(
                    t('sheet.accountNotFound', {
                        defaultValue:
                            "We couldn't find an account with that email. Check the spelling or try a different one.",
                    })
                );
                return;
            }
            setEmailError(undefined);
            setStep('auth-methods');
        },
        [t]
    );

    const handleEditEmail = useCallback(() => {
        setStep('email');
    }, []);

    const handleSelectPassword = useCallback(() => {
        setStep('password');
    }, []);

    const handleSelectGetCode = useCallback(() => {
        if (showCodeDeliveryStep) {
            setStep('send-code');
            return;
        }
        const formData = new FormData();
        formData.append('email', email);
        void sendCodeFetcher.submit(formData, {
            method: 'POST',
            action: resourceRoutes.authorizePasswordlessEmail,
        });
    }, [showCodeDeliveryStep, email, sendCodeFetcher]);

    useEffect(() => {
        if (showCodeDeliveryStep || sendCodeFetcher.state !== 'idle' || !sendCodeFetcher.data) {
            return;
        }
        if (sendCodeFetcher.data.success) {
            setStep('verify-code');
        } else if (sendCodeFetcher.data.requiresLogin) {
            setStep('auth-methods');
        }
    }, [showCodeDeliveryStep, sendCodeFetcher.data, sendCodeFetcher.state]);

    const handleCodeSent = useCallback(() => {
        setStep('verify-code');
    }, []);

    const handleBackToEmail = useCallback(() => {
        setStep('email');
    }, []);

    const handleBackToAuthMethods = useCallback(() => {
        setStep('auth-methods');
    }, []);

    const handleResendOtp = useCallback(() => {
        // Resend is handled internally by VerifyCodeStep via its own fetcher.
    }, []);

    switch (step) {
        case 'account':
            return <AccountStep onNavigate={onSuccess} />;
        case 'email':
            return <EmailStep initialEmail={email} onLookupResult={handleLookupResult} externalError={emailError} />;
        case 'auth-methods':
            return (
                <AuthMethodsStep
                    email={email}
                    onEditEmail={handleEditEmail}
                    onBack={handleBackToEmail}
                    onSelectPassword={handleSelectPassword}
                    onSelectGetCode={handleSelectGetCode}
                    isSendingCode={isSendingCode}
                    sendCodeError={sendCodeError}
                />
            );
        case 'password':
            return (
                <PasswordStep
                    email={email}
                    returnUrl={returnUrl}
                    onBack={handleBackToAuthMethods}
                    onSuccess={onSuccess}
                />
            );
        case 'send-code':
            return (
                <SendCodeStep
                    email={email}
                    onEditEmail={handleEditEmail}
                    onBack={handleBackToAuthMethods}
                    onCodeSent={handleCodeSent}
                />
            );
        case 'verify-code':
            return (
                <VerifyCodeStep
                    email={email}
                    otpLength={otpLength}
                    onSuccess={onSuccess}
                    onResend={handleResendOtp}
                    onBackToAuthMethods={handleBackToAuthMethods}
                />
            );
    }
}

export default function LoginFlow({ variant, ...contentProps }: LoginFlowProps): ReactElement {
    return (
        <LoginFlowVariantProvider variant={variant}>
            <LoginFlowContent {...contentProps} />
        </LoginFlowVariantProvider>
    );
}

export { default as EmailStep } from './steps/email-step';
export { default as AuthMethodsStep } from './steps/auth-methods-step';
export { default as PasswordStep } from './steps/password-step';
export { default as SendCodeStep } from './steps/send-code-step';
export { default as VerifyCodeStep } from './steps/verify-code-step';
export { default as AccountStep } from './steps/account-step';
